import { TokenInfoService } from "src/app/services/authentication/token-info.service";
import { PrivilegeEnum } from "src/app/common/enumerations/privilege-enum";
import { ScheduleStatusEnum } from "../models/enums";
import { Schedule } from "../models/schedule";
import { ScheduleType } from "../models/schedule-type";

/** Schedule actions whose privilege is defined per schedule type in {@link ScheduleValidations.scheduleTypePrivileges}. */
type ScheduleTypeAction = 'createSchedule' | 'deleteSchedule' | 'grantExtension' | 'refuseSchedule' | 'editScheduleDates' | 'editScheduledFor' | 'deleteReminder' | 'subscriberReminder' | 'subscriberExtension';

export class ScheduleValidations {

  // US 6932: Per-schedule-type privileges that replaced the former all-types privileges, keyed by ScheduleTypes.Id
  private static readonly scheduleTypePrivileges: { [scheduleTypeId: number]: Record<ScheduleTypeAction, PrivilegeEnum> } = {
    1: { createSchedule: PrivilegeEnum.CreateScheduleABSI, deleteSchedule: PrivilegeEnum.RemoveScheduleABSI, grantExtension: PrivilegeEnum.GrantExtensionABSI, refuseSchedule: PrivilegeEnum.RefuseExtensionABSI, editScheduleDates: PrivilegeEnum.EditScheduleDatesABSI, editScheduledFor: PrivilegeEnum.EditScheduledForABSI, deleteReminder: PrivilegeEnum.DeleteReminderABSI, subscriberReminder: PrivilegeEnum.SubscribePeopleReminderABSI, subscriberExtension: PrivilegeEnum.SubscribePeopleRequestExtensionABSI },
    2: { createSchedule: PrivilegeEnum.CreateScheduleEPS, deleteSchedule: PrivilegeEnum.RemoveScheduleEPS, grantExtension: PrivilegeEnum.GrantExtensionEPS, refuseSchedule: PrivilegeEnum.RefuseExtensionEPS, editScheduleDates: PrivilegeEnum.EditScheduleDatesEPS, editScheduledFor: PrivilegeEnum.EditScheduledForEPS, deleteReminder: PrivilegeEnum.DeleteReminderEPS, subscriberReminder: PrivilegeEnum.SubscribePeopleReminderEPS, subscriberExtension: PrivilegeEnum.SubscribePeopleRequestExtensionEPS },
    3: { createSchedule: PrivilegeEnum.CreateScheduleTOCA, deleteSchedule: PrivilegeEnum.RemoveScheduleTOCA, grantExtension: PrivilegeEnum.GrantExtensionTOCA, refuseSchedule: PrivilegeEnum.RefuseExtensionTOCA, editScheduleDates: PrivilegeEnum.EditScheduleDatesTOCA, editScheduledFor: PrivilegeEnum.EditScheduledForTOCA, deleteReminder: PrivilegeEnum.DeleteReminderTOCA, subscriberReminder: PrivilegeEnum.SubscribePeopleReminderTOCA, subscriberExtension: PrivilegeEnum.SubscribePeopleRequestExtensionTOCA },
    4: { createSchedule: PrivilegeEnum.CreateScheduleABCRS, deleteSchedule: PrivilegeEnum.RemoveScheduleABCRS, grantExtension: PrivilegeEnum.GrantExtensionABCRS, refuseSchedule: PrivilegeEnum.RefuseExtensionABCRS, editScheduleDates: PrivilegeEnum.EditScheduleDatesABCRS, editScheduledFor: PrivilegeEnum.EditScheduledForABCRS, deleteReminder: PrivilegeEnum.DeleteReminderABCRS, subscriberReminder: PrivilegeEnum.SubscribePeopleReminderABCRS, subscriberExtension: PrivilegeEnum.SubscribePeopleRequestExtensionABCRS },
    5: { createSchedule: PrivilegeEnum.CreateScheduleRSS, deleteSchedule: PrivilegeEnum.RemoveScheduleRSS, grantExtension: PrivilegeEnum.GrantExtensionRSS, refuseSchedule: PrivilegeEnum.RefuseExtensionRSS, editScheduleDates: PrivilegeEnum.EditScheduleDatesRSS, editScheduledFor: PrivilegeEnum.EditScheduledForRSS, deleteReminder: PrivilegeEnum.DeleteReminderRSS, subscriberReminder: PrivilegeEnum.SubscribePeopleReminderRSS, subscriberExtension: PrivilegeEnum.SubscribePeopleRequestExtensionRSS },
    7: { createSchedule: PrivilegeEnum.CreateScheduleLOTO, deleteSchedule: PrivilegeEnum.RemoveScheduleLOTO, grantExtension: PrivilegeEnum.GrantExtensionLOTO, refuseSchedule: PrivilegeEnum.RefuseExtensionLOTO, editScheduleDates: PrivilegeEnum.EditScheduleDatesLOTO, editScheduledFor: PrivilegeEnum.EditScheduledForLOTO, deleteReminder: PrivilegeEnum.DeleteReminderLOTO, subscriberReminder: PrivilegeEnum.SubscribePeopleReminderLOTO, subscriberExtension: PrivilegeEnum.SubscribePeopleRequestExtensionLOTO },
    13: { createSchedule: PrivilegeEnum.CreateScheduleSRVIS, deleteSchedule: PrivilegeEnum.RemoveScheduleSRVIS, grantExtension: PrivilegeEnum.GrantExtensionSRVIS, refuseSchedule: PrivilegeEnum.RefuseExtensionSRVIS, editScheduleDates: PrivilegeEnum.EditScheduleDatesSRVIS, editScheduledFor: PrivilegeEnum.EditScheduledForSRVIS, deleteReminder: PrivilegeEnum.DeleteReminderSRVIS, subscriberReminder: PrivilegeEnum.SubscribePeopleReminderSRVIS, subscriberExtension: PrivilegeEnum.SubscribePeopleRequestExtensionSRVIS },
  };

  /**
   * Checks the privilege that grants an action for the given schedule type.
   * Unknown or inactive schedule types (e.g. BRATOI) have no privileges, so the action is denied.
   * @param action Schedule action to check.
   * @param tokenInfo Current user's token, used to read privileges.
   * @param scheduleType Schedule type the action applies to.
   */
  private static hasScheduleTypePrivilege(action: ScheduleTypeAction, tokenInfo: TokenInfoService, scheduleType?: ScheduleType): boolean {
    const privilege = scheduleType?.id != null ? this.scheduleTypePrivileges[scheduleType.id]?.[action] : undefined;
    return privilege != null && tokenInfo.hasPrivilege(privilege) === true;
  }

  static validateRole(controlName: string, tokenInfo: TokenInfoService, scheduleType?: ScheduleType, schedule: Schedule | null = null): boolean {
    const privileges: PrivilegeEnum[] = tokenInfo.getPrivileges() ?? [];
    if (!privileges.length) {
      return false;
    }
    switch (controlName) {
      // US 6932: Generic actions now check the privilege of the current schedule type
      case 'createSchedule':
      case 'deleteSchedule':
      case 'refuseSchedule':
      case 'editScheduleDates':
      case 'editScheduledFor':
      case 'deleteReminder':
      case 'subscriberReminder':
      case 'subscriberExtension':
        return this.hasScheduleTypePrivilege(controlName as ScheduleTypeAction, tokenInfo, scheduleType);
      case 'updateSchedule':
        switch (scheduleType?.id) {
          case 1:
            if (tokenInfo.hasPrivilege(PrivilegeEnum.EditABSISchedule)) {
              if (schedule != null && schedule?.statusId !== ScheduleStatusEnum.completed) {
                return true;
              }
            }
            return false;
          case 2:
            if (tokenInfo.hasPrivilege(PrivilegeEnum.EditEPS)) {
              if (schedule != null && schedule?.statusId !== ScheduleStatusEnum.completed) {
                return true;
              }
            }
            return false;
          case 3:
            if (tokenInfo.hasPrivilege(PrivilegeEnum.EditTOCA)) {
              if (schedule != null && schedule?.statusId !== ScheduleStatusEnum.completed) {
                return true;
              }
            }
            return false;
          case 4:
            if (tokenInfo.hasPrivilege(PrivilegeEnum.EditABCRS)) {
              if (schedule != null && schedule?.statusId !== ScheduleStatusEnum.completed) {
                return true;
              }
            }
            return false;
          case 5:
            if (tokenInfo.hasPrivilege(PrivilegeEnum.EditRSS)) {
              if (schedule != null && schedule?.statusId !== ScheduleStatusEnum.completed) {
                return true;
              }
            }
            return false;
          case 7:
            if (tokenInfo.hasPrivilege(PrivilegeEnum.EditLOTO)) {
              if (schedule != null && schedule?.statusId !== ScheduleStatusEnum.completed) {
                return true;
              }
            }
            return false;
          case 13:
            if (tokenInfo.hasPrivilege(PrivilegeEnum.EditSRVIS)) {
              if (schedule != null && schedule?.statusId !== ScheduleStatusEnum.completed) {
                return true;
              }
            }
            return false;
        }
        return false;
      case 'completeSchedule':
        switch (scheduleType?.id) {
          case 1:
            if (tokenInfo.hasPrivilege(PrivilegeEnum.CompleteABSI)) {
              return true;
            }
            return false;
          case 2:
            if (tokenInfo.hasPrivilege(PrivilegeEnum.CompleteEPS)) {
              return true;
            }
            return false;
          case 3:
            if (tokenInfo.hasPrivilege(PrivilegeEnum.CompleteTOCA)) {
              return true;
            }
            return false;
          case 4:
            if (tokenInfo.hasPrivilege(PrivilegeEnum.CompleteABCRS)) {
              return true;
            }
            return false;
          case 5:
            if (tokenInfo.hasPrivilege(PrivilegeEnum.CompleteRSS)) {
              return true;
            }
            return false;
          case 7:
            if (tokenInfo.hasPrivilege(PrivilegeEnum.CompleteLOTO)) {
              return true;
            }
            return false;
          case 13:
            if (tokenInfo.hasPrivilege(PrivilegeEnum.CompleteSRVIS)) {
              return true;
            }
            return false;
        }
        return false;
      case 'createNote':
        switch (scheduleType?.id) {
          case 1:
            if (tokenInfo.hasPrivilege(PrivilegeEnum.CreateNoteABSI)) {
              return true;
            }
            return false;
          case 2:
            if (tokenInfo.hasPrivilege(PrivilegeEnum.CreateNoteEPS)) {
              return true;
            }
            return false;
          case 3:
            if (tokenInfo.hasPrivilege(PrivilegeEnum.CreateNoteTOCA)) {
              return true;
            }
            return false;
          case 4:
            if (tokenInfo.hasPrivilege(PrivilegeEnum.CreateNoteABCRS)) {
              return true;
            }
            return false;
          case 5:
            if (tokenInfo.hasPrivilege(PrivilegeEnum.CreateNoteRSS)) {
              return true;
            }
            return false;
          case 7:
            if (tokenInfo.hasPrivilege(PrivilegeEnum.CreateNoteLOTO)) {
              return true;
            }
            return false;
          case 13:
            if (tokenInfo.hasPrivilege(PrivilegeEnum.CreateNoteSRVIS)) {
              return true;
            }
            return false;
          default:
            return true;
        }
        return false;
      case 'requestExtension':
        switch (scheduleType?.id) {
          case 1:
            if (tokenInfo.hasPrivilege(PrivilegeEnum.RequestExtensionABSI)) {
              return true;
            }
            return false;
          case 2:
            if (tokenInfo.hasPrivilege(PrivilegeEnum.RequestExtensionEPS)) {
              return true;
            }
            return false;
          case 3:
            if (tokenInfo.hasPrivilege(PrivilegeEnum.RequestExtensionTOCA)) {
              return true;
            }
            return false;
          case 4:
            if (tokenInfo.hasPrivilege(PrivilegeEnum.RequestExtensionABCRS)) {
              return true;
            }
            return false;
          case 5:
            if (tokenInfo.hasPrivilege(PrivilegeEnum.RequestExtensionRSS)) {
              return true;
            }
            return false;
          case 7:
            if (tokenInfo.hasPrivilege(PrivilegeEnum.RequestExtensionLOTO)) {
              return true;
            }
            return false;
          case 13:
            if (tokenInfo.hasPrivilege(PrivilegeEnum.RequestExtensionSRVIS)) {
              return true;
            }
            return false;
        }
        return false;
      case 'grantExtension':
        // US 6932: Grant Extension is checked per schedule type
        if (this.hasScheduleTypePrivilege('grantExtension', tokenInfo, scheduleType)) {
          if (schedule != null && schedule?.statusId === ScheduleStatusEnum.requestExtension) {
            return true;
          }
        }
        return false;
      case 'grantExtensionSubdetail':
        if (this.hasScheduleTypePrivilege('grantExtension', tokenInfo, scheduleType)) {
          if (schedule != null && schedule?.subdetail?.statusId === ScheduleStatusEnum.requestExtension) {
            return true;
          }
        }
        return false;
      case 'updateReminder':
        switch (scheduleType?.id) {
          case 1:
            if (tokenInfo.hasPrivilege(PrivilegeEnum.EditReminderABSI)) {
              return true;
            }
            return false;
          case 2:
            if (tokenInfo.hasPrivilege(PrivilegeEnum.EditReminderEPS)) {
              return true;
            }
            return false;
          case 3:
            if (tokenInfo.hasPrivilege(PrivilegeEnum.EditReminderTOCA)) {
              return true;
            }
            return false;
          case 4:
            if (tokenInfo.hasPrivilege(PrivilegeEnum.EditReminderABCRS)) {
              return true;
            }
            return false;
          case 5:
            if (tokenInfo.hasPrivilege(PrivilegeEnum.EditReminderRSS)) {
              return true;
            }
            return false;
          case 7:
            if (tokenInfo.hasPrivilege(PrivilegeEnum.EditReminderLOTO)) {
              return true;
            }
            return false;
          case 13:
            if (tokenInfo.hasPrivilege(PrivilegeEnum.EditReminderSRVIS)) {
              return true;
            }
            return false;
        }
        return false;
      case 'createReminder':
        switch (scheduleType?.id) {
          case 1:
            if (tokenInfo.hasPrivilege(PrivilegeEnum.AddReminderABSI)) {
              return true;
            }
            return false;
          case 2:
            if (tokenInfo.hasPrivilege(PrivilegeEnum.AddReminderEPS)) {
              return true;
            }
            return false;
          case 3:
            if (tokenInfo.hasPrivilege(PrivilegeEnum.AddReminderTOCA)) {
              return true;
            }
            return false;
          case 4:
            if (tokenInfo.hasPrivilege(PrivilegeEnum.AddReminderABCRS)) {
              return true;
            }
            return false;
          case 5:
            if (tokenInfo.hasPrivilege(PrivilegeEnum.AddReminderRSS)) {
              return true;
            }
            return false;
          case 7:
            if (tokenInfo.hasPrivilege(PrivilegeEnum.AddReminderLOTO)) {
              return true;
            }
            return false;
          case 13:
            if (tokenInfo.hasPrivilege(PrivilegeEnum.AddReminderSRVIS)) {
              return true;
            }
            return false;
        }
        return false;
    }
    return false;
  }

  static validateCommons(codes: string): boolean {
    if (codes.includes('ADM') || codes.includes('PC') || codes.includes('SA') || codes.includes('DAO')) {
      return true;
    }
    return false;
  }
}
